// Custom Hook for F1 World Championship Data (1950-2020)
import { useState, useEffect, useCallback, useMemo } from 'react';
import { loadF1ChampionshipData, loadAllF1ChampionshipData, getF1ChampionshipStats, filterF1Data } from '../utils/f1ChampionshipLoader';

/**
 * Custom hook for loading F1 World Championship data
 * @param {string|Array|Object} files - File(s) to load or filter criteria
 * @param {Object} options - Loading options
 * @returns {Object} - Data, loading state, error state, and utility functions
 */
export const useF1Championship = (files = null, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let result;
      if (files === null || files === undefined) {
        // Load all key files
        result = await loadAllF1ChampionshipData();
      } else if (typeof files === 'string') {
        // Load single file
        result = { [files]: await loadF1ChampionshipData(files) };
      } else if (Array.isArray(files)) {
        // Load multiple specific files
        const promises = files.map(file => loadF1ChampionshipData(file));
        const results = await Promise.all(promises);
        result = results.reduce((acc, data, index) => {
          acc[files[index].replace('.csv', '')] = data.data;
          return acc;
        }, {});
      } else if (typeof files === 'object' && files.type) {
        // Apply filters
        const allData = await loadAllF1ChampionshipData();
        result = filterF1Data(allData, files);
      }
      
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load F1 championship data');
      console.error('F1 championship loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [files]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refetch = useCallback(() => {
    loadData();
  }, [loadData]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Get filtered data when filters are applied
  const filteredData = useMemo(() => {
    if (Object.keys(filters).length === 0) return data;
    return filterF1Data(data, filters);
  }, [data, filters]);

  // Get statistics
  const stats = useMemo(() => {
    return data ? getF1ChampionshipStats(data) : null;
  }, [data]);

  return { 
    data, 
    filteredData, 
    loading, 
    error, 
    refetch, 
    filters, 
    updateFilters, 
    clearFilters, 
    stats 
  };
};

/**
 * Hook for specific F1 championship data views
 * @param {string} viewType - Type of view ('races', 'drivers', 'standings', 'circuits')
 * @param {Object} options - View-specific options
 * @returns {Object} - View-specific data and functions
 */
export const useF1ChampionshipView = (viewType, options = {}) => {
  const { data, loading, error, refetch } = useF1Championship([
    `${viewType}.csv`
  ]);

  const viewData = useMemo(() => {
    if (!data) return null;
    return data[viewType] || [];
  }, [data, viewType]);

  const getViewById = useCallback((id) => {
    if (!viewData) return null;
    return viewData.find(item => {
      switch (viewType) {
        case 'races':
          return item.raceId == id || item.round == id;
        case 'drivers':
          return item.driverId == id;
        case 'constructors':
          return item.constructorId == id;
        case 'circuits':
          return item.circuitId == id;
        default:
          return item.id == id;
      }
    });
  }, [viewData, viewType]);

  const getTopItems = useCallback((limit = 10, sortBy = 'wins') => {
    if (!viewData) return [];
    
    return [...viewData]
      .sort((a, b) => {
        switch (sortBy) {
          case 'wins':
            return (b.wins || 0) - (a.wins || 0);
          case 'points':
            return (b.points || 0) - (a.points || 0);
          case 'name':
            return a.name?.localeCompare(b.name || '');
          default:
            return 0;
        }
      })
      .slice(0, limit);
  }, [viewData, viewType]);

  return {
    data: viewData,
    loading,
    error,
    refetch,
    getViewById,
    getTopItems,
    viewType
  };
};
